/**
 * @fileoverview Expand top-level audit ChangeDetail objects into readable nested rows.
 * @module ui-admin/utils/expandAuditChanges
 *
 * Responsibilities:
 * - Flatten nested object diffs (e.g. case `responses`) into one row per leaf change.
 * - Expand identity-keyed object arrays (modules, contacts) into per-element
 *   diff rows (e.g. `modules.hydrant.isEnabled: No → Yes`); primitive arrays
 *   and unkeyed object arrays stay as a single row.
 * - Cap nested row count so large maps cannot overflow the Field Changes table.
 *
 * Dependencies:
 * - @firesquirrel/audit ChangeDetail type.
 *
 * Architecture:
 * - Presentation-only. Does not rewrite stored Dexie/Mongo audit rows.
 * - Consumed by AuditLogDetailPanel and AuditLogTimeline.
 *
 * Notes:
 * - Existing logs store nested maps as a single `field: 'responses'` change.
 *   Display expansion is required for those historical rows as well as new writes.
 */

import type { ChangeDetail } from '@firesquirrel/audit';
import { isInspectionResponse } from './formatAuditValue';

const MAX_NESTED_DEPTH = 4;
const MAX_EXPANDED_ROWS = 40;

// Keys probed (in order) to derive a stable identity for array elements so
// element-level diffs can be emitted as `field.<key>` rows (e.g. module
// toggles → "modules.hydrant.isEnabled: No → Yes").
const ARRAY_IDENTITY_KEYS = ['code', '_id', 'id', 'key', 'lookupId', 'name'];

function arrayItemKey(item: Record<string, unknown>): string | null {
  for (const k of ARRAY_IDENTITY_KEYS) {
    const v = item[k];
    if ((typeof v === 'string' || typeof v === 'number') && String(v).length > 0) {
      return String(v);
    }
  }
  return null;
}

/**
 * Pair elements of two object arrays by identity key and emit per-element
 * diff rows. Returns null when the arrays are not safely keyed (non-object
 * elements, missing/duplicate identities) — callers then keep the original
 * whole-array change row unchanged.
 */
function collectKeyedArrayDiffs(
  path: string,
  oldArr: unknown[],
  newArr: unknown[],
  out: ChangeDetail[],
): boolean {
  const toKeyedMap = (arr: unknown[]): Map<string, Record<string, unknown>> | null => {
    const map = new Map<string, Record<string, unknown>>();
    for (const item of arr) {
      if (!isPlainObject(item)) return null;
      const key = arrayItemKey(item);
      if (key === null || map.has(key)) return null;
      map.set(key, item);
    }
    return map;
  };

  const oldMap = toKeyedMap(oldArr);
  const newMap = toKeyedMap(newArr);
  if (!oldMap || !newMap) return false;

  const temp: ChangeDetail[] = [];
  for (const [key, newItem] of newMap) {
    const oldItem = oldMap.get(key);
    if (!oldItem) {
      temp.push({ field: `${path}.${key}`, oldValue: null, newValue: newItem, fieldType: getFieldType(newItem) });
    } else {
      collectNestedDiffs(`${path}.${key}`, oldItem, newItem, 1, temp);
    }
    if (temp.length >= MAX_EXPANDED_ROWS) return false;
  }
  for (const [key, oldItem] of oldMap) {
    if (!newMap.has(key)) {
      temp.push({ field: `${path}.${key}`, oldValue: oldItem, newValue: null, fieldType: getFieldType(oldItem) });
      if (temp.length >= MAX_EXPANDED_ROWS) return false;
    }
  }
  out.push(...temp);
  return true;
}

function isPlainObject(val: unknown): val is Record<string, unknown> {
  return !!val && typeof val === 'object' && !Array.isArray(val);
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'object' && typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
}

function getFieldType(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  if (value instanceof Date) return 'date';
  return typeof value;
}

function isInspectionResponseValue(val: unknown): boolean {
  return isPlainObject(val) && isInspectionResponse(val);
}

function looksLikeGeneratedFieldId(value: string): boolean {
  return /^(fld[_-]|field-\d)/i.test(value) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(value);
}

function inspectionAnswersEqual(oldVal: unknown, newVal: unknown): boolean {
  const oldObj = isPlainObject(oldVal) ? oldVal : null;
  const newObj = isPlainObject(newVal) ? newVal : null;
  const oldNA = oldObj?.isNA === true;
  const newNA = newObj?.isNA === true;
  if (oldNA && newNA) return true;
  if (oldNA !== newNA) return false;
  return valuesEqual(oldObj ? oldObj.value : oldVal, newObj ? newObj.value : newVal);
}

function inspectionQuestionLabel(key: string, oldVal: unknown, newVal: unknown): string {
  for (const rec of [newVal, oldVal]) {
    if (!isPlainObject(rec)) continue;
    for (const labelKey of ['questionText', 'label', 'question']) {
      const label = rec[labelKey];
      if (typeof label === 'string' && label.trim() && !looksLikeGeneratedFieldId(label.trim())) {
        return label.trim();
      }
    }
  }
  if (!looksLikeGeneratedFieldId(key)) return key;
  return 'Inspection answer';
}

function collectNestedDiffs(
  path: string,
  oldVal: unknown,
  newVal: unknown,
  depth: number,
  out: ChangeDetail[],
): void {
  if (out.length >= MAX_EXPANDED_ROWS) return;

  if (isInspectionResponseValue(oldVal) || isInspectionResponseValue(newVal)) {
    if (inspectionAnswersEqual(oldVal, newVal)) return;
    out.push({
      field: path,
      oldValue: oldVal ?? null,
      newValue: newVal ?? null,
      fieldType: getFieldType(newVal ?? oldVal),
    });
    return;
  }

  if (valuesEqual(oldVal, newVal)) return;

  const bothObjects = isPlainObject(oldVal) && isPlainObject(newVal);
  const oneEmptyObject =
    (isPlainObject(oldVal) && (newVal == null || isPlainObject(newVal)))
    || (isPlainObject(newVal) && (oldVal == null || isPlainObject(oldVal)));

  if (depth < MAX_NESTED_DEPTH && (bothObjects || oneEmptyObject)) {
    const oldObj = isPlainObject(oldVal) ? oldVal : {};
    const newObj = isPlainObject(newVal) ? newVal : {};
    const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    let nested = false;
    for (const key of keys) {
      if (key.startsWith('_')) continue;
      nested = true;
      const childOld = oldObj[key];
      const childNew = newObj[key];
      const childSegment = (isInspectionResponseValue(childOld) || isInspectionResponseValue(childNew))
        ? inspectionQuestionLabel(key, childOld, childNew)
        : key;
      const childPath = path && path !== 'responses' ? `${path}.${childSegment}` : childSegment;
      collectNestedDiffs(childPath, childOld, childNew, depth + 1, out);
      if (out.length >= MAX_EXPANDED_ROWS) return;
    }
    if (nested) return;
  }

  out.push({
    field: path,
    oldValue: oldVal ?? null,
    newValue: newVal ?? null,
    fieldType: getFieldType(newVal ?? oldVal),
  });
}

/**
 * @process expandAuditChanges
 *
 * Steps:
 * 1. Copy primitive and array changes as-is.
 * 2. For object-valued changes, walk keys and emit one row per differing leaf.
 * 3. Stop at MAX_NESTED_DEPTH / MAX_EXPANDED_ROWS.
 *
 * Inputs:
 * - changes: ChangeDetail[] stored on an AuditLogEntry.
 *
 * Outputs:
 * - ChangeDetail[] suitable for Field Changes table / timeline inline diffs.
 *
 * Failure Handling:
 * - Never throws. Unknown values are kept as a single row.
 *
 * Idempotency: REQUIRED — same inputs always produce the same rows.
 */
export function expandAuditChanges(changes: ChangeDetail[]): ChangeDetail[] {
  const expanded: ChangeDetail[] = [];

  for (const change of changes) {
    if (expanded.length >= MAX_EXPANDED_ROWS) break;

    const oldVal = change.oldValue;
    const newVal = change.newValue;

    // Arrays of identity-keyed objects (tenant modules, contacts, etc.)
    // expand into per-element rows; anything else stays a single row.
    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      const before = expanded.length;
      if (collectKeyedArrayDiffs(change.field, oldVal, newVal, expanded)) {
        if (expanded.length === before) expanded.push(change);
        continue;
      }
      expanded.push(change);
      continue;
    }

    const expandable =
      (isPlainObject(oldVal) || isPlainObject(newVal))
      && !Array.isArray(oldVal)
      && !Array.isArray(newVal)
      && !isInspectionResponseValue(oldVal)
      && !isInspectionResponseValue(newVal);

    if (!expandable) {
      expanded.push(change);
      continue;
    }

    const beforeCount = expanded.length;
    collectNestedDiffs(change.field, oldVal, newVal, 0, expanded);
    if (expanded.length === beforeCount) {
      if (change.field === 'responses') continue;
      expanded.push(change);
    }
  }

  return expanded;
}
